import { Form, FormInstance } from 'antd';
import { useCreateDossierMutation } from 'hooks/dossiers.hooks';
import useFormSubmittable from 'hooks/useFormSubmittable';
import useMinimizeAnimation from 'hooks/useMinimizeAnimation';
import useStepTransition from 'hooks/useStepTransition';
import { useCallback, useState } from 'react';
import type { RefObject } from 'react';
import { CreateDossierLead } from 'types/Dossier';
import { mapDropzoneFiles } from 'utils/files';
import {
  BeneficiaryFormType,
  SiteContactInfo,
} from 'views/dossiers/ajout/forms/Beneficiary.form';
import { ProjectInformationsFormType } from 'views/dossiers/ajout/forms/ProjectInformations.form';
import {
  SiteAddressInformationsType,
  SiteContactInformationsType,
  SiteInformationsType,
} from 'views/dossiers/ajout/forms/SiteInformations.form';
import useAjoutDossierDraft from 'views/dossiers/ajout/hooks/useAjoutDossierDraft';

const NB_STEPS = 3;

interface UseAjoutDossierFormControllerParams {
  openRequestId: number;
  onSuccess?: () => void;
}

interface UseAjoutDossierFormControllerReturn {
  state: {
    isHydrated: boolean;
    isSuccessModalOpen: boolean;
    isCloseConfirmationOpen: boolean;
    isDraftActive: boolean;
    isDraftMinimized: boolean;
    currentSlide: number;
    isSiteStepComplete: boolean;
    siteFormUiResetKey: number;
    isFormSubmittable: boolean;
    isLoading: boolean;
    isError: boolean;
    error?: Error | null;
  };
  ui: {
    direction: 'next' | 'prev';
    transitionKey: number;
    isMinimizing: boolean;
    siteContactInfo?: SiteContactInfo;
    minimizeTargetRef: RefObject<HTMLDivElement>;
  };
  forms: {
    siteContactFormInstance: FormInstance<SiteContactInformationsType>;
    siteAddressFormInstance: FormInstance<SiteAddressInformationsType>;
    projectFormInstance: FormInstance<ProjectInformationsFormType>;
    beneficiaryFormInstance: FormInstance<BeneficiaryFormType>;
  };
  actions: {
    requestMinimize: () => void;
    expandDraft: () => void;
    handleNext: () => void;
    handlePrevious: () => void;
    handleCloseRequest: () => void;
    handleCancelCloseRequest: () => void;
    handleConfirmClose: () => void;
    handleCloseSuccessModal: () => void;
    handleSiteCompletionChange: (isComplete: boolean) => void;
    handleSiteValuesChange: (
      changedValues: Partial<SiteInformationsType>,
      allValues: SiteInformationsType,
    ) => void;
    handleProjectValuesChange: (
      changedValues: Partial<ProjectInformationsFormType>,
      allValues: ProjectInformationsFormType,
    ) => void;
    handleBeneficiaryValuesChange: (
      changedValues: Partial<BeneficiaryFormType>,
      allValues: BeneficiaryFormType,
    ) => void;
  };
}

export default function useAjoutDossierFormController({
  openRequestId,
  onSuccess,
}: UseAjoutDossierFormControllerParams): UseAjoutDossierFormControllerReturn {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [siteFormUiResetKey, setSiteFormUiResetKey] = useState(0);

  const [siteContactFormInstance] = Form.useForm<SiteContactInformationsType>();
  const [siteAddressFormInstance] = Form.useForm<SiteAddressInformationsType>();
  const [projectFormInstance] = Form.useForm<ProjectInformationsFormType>();
  const [beneficiaryFormInstance] = Form.useForm<BeneficiaryFormType>();
  const { direction, transitionKey, triggerNext, triggerPrevious } =
    useStepTransition();

  const isProjectFormSubmittable = useFormSubmittable(projectFormInstance);
  const isBeneficiaryFormSubmittable = useFormSubmittable(
    beneficiaryFormInstance,
  );

  const { mutate, isLoading, isError, error, reset } =
    useCreateDossierMutation();

  const resetForms = useCallback(() => {
    siteContactFormInstance.resetFields();
    siteAddressFormInstance.resetFields();
    projectFormInstance.resetFields();
    beneficiaryFormInstance.resetFields();
  }, [
    beneficiaryFormInstance,
    projectFormInstance,
    siteAddressFormInstance,
    siteContactFormInstance,
  ]);

  const {
    state: {
      isHydrated,
      isDraftActive,
      isDraftMinimized,
      currentSlide,
      isSiteStepComplete,
      siteContactInfo,
    },
    actions: {
      expandDraft,
      minimizeDraft,
      clearDraft,
      clearPersistedDraft,
      clearPersistedFiles,
      persistCurrentValues,
      updateCurrentSlide,
      handleSiteCompletionChange,
      handleSiteValuesChange,
      handleProjectValuesChange,
      handleBeneficiaryValuesChange,
    },
  } = useAjoutDossierDraft({
    openRequestId,
    siteContactFormInstance,
    siteAddressFormInstance,
    projectFormInstance,
    beneficiaryFormInstance,
    resetForms,
    resetMutationState: reset,
    onBeforeOpen: () => {
      setIsSuccessModalOpen(false);
    },
  });

  const resetSiteStepCompletionState = useCallback(() => {
    // Reset local UI sections in SiteInformationsForm.
    setSiteFormUiResetKey((previous) => previous + 1);
  }, []);

  const isFormSubmittable =
    (currentSlide === 0 && isSiteStepComplete) ||
    (currentSlide === 1 && isProjectFormSubmittable) ||
    (currentSlide === 2 && isBeneficiaryFormSubmittable);

  const {
    isMinimizing,
    requestMinimize,
    targetRef: minimizeTargetRef,
  } = useMinimizeAnimation({
    isMinimized: isDraftMinimized,
    sourceSelector: '.ajout-dossier-form-modal .ant-modal-content',
    onBeforeMinimize: persistCurrentValues,
    onMinimize: minimizeDraft,
  });

  const handleFinish = useCallback(
    async (beneficiaryValues?: BeneficiaryFormType) => {
      const currentStep1Values = {
        ...siteContactFormInstance.getFieldsValue(true),
        ...siteAddressFormInstance.getFieldsValue(true),
      } as SiteInformationsType;
      const currentProjectValues = projectFormInstance.getFieldsValue(
        true,
      ) as ProjectInformationsFormType;

      if (
        !beneficiaryValues ||
        !currentStep1Values.dateDebutTravaux ||
        !currentStep1Values.dateFinTravaux
      ) {
        return;
      }

      const filesToSubmit =
        beneficiaryValues.files?.filter(
          (file) => file.originFileObj instanceof File,
        ) || [];
      const files = filesToSubmit.length
        ? await mapDropzoneFiles(filesToSubmit)
        : [];
      // Mapping explicite des 3 formulaires vers le contrat API CreateDossierLead.
      const payload: CreateDossierLead = {
        dossier: {
          referentCommercial: {
            email: currentStep1Values.interlocuteurChantierEmail,
            firstname: currentStep1Values.interlocuteurChantierFirstname,
            lastname: currentStep1Values.interlocuteurChantierLastname,
          },
          batiment: {
            ancien: currentProjectValues.isAncien === 'oui',
            ...(currentProjectValues.operation === 'Chauffage'
              ? {
                  ancienneEnergieChauffage:
                    currentProjectValues.ancienneEnergieChauffage,
                  nouvelleEnergieChauffage:
                    currentProjectValues.nouvelleEnergieChauffage,
                  surfaceChauffee: currentProjectValues.surfaceChauffee,
                  surfaceChauffeeRehabilitee:
                    currentProjectValues.surfaceChauffeeRehabilitee,
                }
              : currentProjectValues.operation === 'Ventilation'
                ? {
                    surfaceVentileeRehabilitee:
                      currentProjectValues.surfaceVentileeRehabilitee,
                  }
                : {}),
          },
          chantier: {
            natureTravaux: currentProjectValues.nature,
            dateDebutTravaux: currentStep1Values.dateDebutTravaux.toISOString(),
            dateFinTravaux: currentStep1Values.dateFinTravaux.toISOString(),
            ...(beneficiaryValues.own_company === 'non'
              ? {
                  entreprise: {
                    raisonSociale: beneficiaryValues.raisonSocialeETX,
                    adresse: beneficiaryValues.adresseETX,
                    codePostal: beneficiaryValues.codePostalETX,
                    ville: beneficiaryValues.villeETX,
                    telephone: beneficiaryValues.telephoneETX,
                    email: beneficiaryValues.emailETX,
                  },
                }
              : {}),
          },
          beneficiaire: {
            civilite: beneficiaryValues.civilite,
            lastname: beneficiaryValues.lastname,
            firstname: beneficiaryValues.firstname,
            email: beneficiaryValues.email,
            telephone: beneficiaryValues.telephone,
            raisonSociale: currentStep1Values.raisonSocialeBeneficiaire,
          },
          site: {
            nom: currentStep1Values.nomSite,
            usage: currentProjectValues.usage,
            adresse: currentStep1Values.adresseSite,
            codePostal: currentStep1Values.codePostalSite,
            ville: currentStep1Values.villeSite,
          },
          description: beneficiaryValues.descriptionProjet,
          modeleIncitationFinanciere:
            beneficiaryValues.modeleIncitationFinanciere,
          typeOperation: currentProjectValues.operation,
        },
        files,
      };
      mutate(payload, {
        onSuccess: () => {
          clearPersistedFiles();
          clearPersistedDraft();
          clearDraft();
          resetForms();
          resetSiteStepCompletionState();
          setIsSuccessModalOpen(true);
          onSuccess?.();
        },
      });
    },
    [
      clearDraft,
      clearPersistedDraft,
      clearPersistedFiles,
      mutate,
      onSuccess,
      projectFormInstance,
      resetSiteStepCompletionState,
      resetForms,
      siteAddressFormInstance,
      siteContactFormInstance,
    ],
  );

  const handleNext = useCallback(() => {
    if (currentSlide === NB_STEPS - 1) {
      const beneficiaryValues = beneficiaryFormInstance.getFieldsValue(
        true,
      ) as BeneficiaryFormType;
      handleFinish(beneficiaryValues);
      return;
    }

    persistCurrentValues();
    triggerNext();
    updateCurrentSlide(currentSlide + 1);
  }, [
    beneficiaryFormInstance,
    currentSlide,
    handleFinish,
    persistCurrentValues,
    triggerNext,
    updateCurrentSlide,
  ]);

  const handlePrevious = useCallback(() => {
    if (currentSlide === 0) {
      setIsCloseConfirmationOpen(true);
      return;
    }

    triggerPrevious();
    updateCurrentSlide(Math.max(0, currentSlide - 1));
  }, [currentSlide, triggerPrevious, updateCurrentSlide]);

  const handleCloseRequest = useCallback(() => {
    setIsCloseConfirmationOpen(true);
  }, []);

  const handleCancelCloseRequest = useCallback(() => {
    setIsCloseConfirmationOpen(false);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setIsCloseConfirmationOpen(false);
    clearPersistedFiles();
    clearPersistedDraft();
    clearDraft();
    // Reset UI et mutation ici pour garder la fermeture explicite (sans effet implicite).
    resetForms();
    resetSiteStepCompletionState();
    reset();
  }, [
    clearDraft,
    clearPersistedDraft,
    clearPersistedFiles,
    reset,
    resetForms,
    resetSiteStepCompletionState,
  ]);

  const handleCloseSuccessModal = useCallback(() => {
    resetSiteStepCompletionState();
    setIsSuccessModalOpen(false);
  }, [resetSiteStepCompletionState]);

  return {
    state: {
      isHydrated,
      isSuccessModalOpen,
      isCloseConfirmationOpen,
      isDraftActive,
      isDraftMinimized,
      currentSlide,
      isSiteStepComplete,
      siteFormUiResetKey,
      isFormSubmittable,
      isLoading,
      isError,
      error: error as Error | null,
    },
    ui: {
      direction,
      transitionKey,
      isMinimizing,
      siteContactInfo,
      minimizeTargetRef,
    },
    forms: {
      siteContactFormInstance,
      siteAddressFormInstance,
      projectFormInstance,
      beneficiaryFormInstance,
    },
    actions: {
      requestMinimize,
      expandDraft,
      handleNext,
      handlePrevious,
      handleCloseRequest,
      handleCancelCloseRequest,
      handleConfirmClose,
      handleCloseSuccessModal,
      handleSiteCompletionChange,
      handleSiteValuesChange,
      handleProjectValuesChange,
      handleBeneficiaryValuesChange,
    },
  };
}
